import React, { useState, useRef, useEffect } from 'react';
import {MessageCircleDashed as MessageCircle, X, Send, Loader2} from 'lucide-react';
import { lumi } from '../lib/lumi';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Bonjour ! 👋 Je suis l\'assistant virtuel de No Limit Fitness. Comment puis-je vous aider aujourd\'hui ?',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await lumi.functions.invoke('chatbot', {
        method: 'POST',
        responseType: 'stream',
        body: {
          messages: [...messages, userMessage].map((msg) => ({
            role: msg.role,
            content: msg.content,
          })),
        },
      });

      if (!response) {
        console.error('Empty response stream');
        throw new Error('Empty response stream');
      }

      const parsedStream = await lumi.tools.ai.parseStream(response);
      const reader = parsedStream.getReader();

      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = value?.data?.content ?? '';
          
          setMessages((prev) => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              role: 'assistant',
              content: newMessages[newMessages.length - 1].content + chunk,
            };
            return newMessages;
          });
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error: any) {
      console.error('Chatbot error:', error);
      const errorMessage = error?.data?.error || error?.message || 'Erreur inconnue';
      console.error('Error details:', errorMessage);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Désolé, une erreur s\'est produite. Contactez-nous au 03 83 64 00 00.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chatbot Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-nolimit-red hover:bg-red-600 text-white rounded-full p-4 shadow-2xl transition-all transform hover:scale-110 group"
          aria-label="Ouvrir le chat"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 bg-white text-nolimit-red text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            !
          </span>
        </button>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-3rem)] bg-zinc-900 rounded-2xl shadow-2xl border border-white/10 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-nolimit-red p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white rounded-full p-2">
                <MessageCircle className="h-5 w-5 text-nolimit-red" />
              </div>
              <div>
                <h3 className="font-bold text-white">Assistant No Limit</h3>
                <p className="text-xs text-white/80">En ligne</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
              aria-label="Fermer le chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-nolimit-red text-white'
                      : 'bg-zinc-800 text-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 rounded-2xl px-4 py-3">
                  <Loader2 className="h-5 w-5 text-nolimit-red animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Posez votre question..."
                className="flex-1 bg-zinc-800 text-white rounded-full px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-nolimit-red"
                disabled={isLoading}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="bg-nolimit-red hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full p-3 transition-colors"
                aria-label="Envoyer"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Propulsé par l'IA No Limit Fitness
            </p>
          </div>
        </div>
      )}
    </>
  );
};
